import { DownOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Menu } from 'antd';
import { Dropdown } from 'antd5';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImageNavButtonDisabledContext } from 'components/damages/ImageNavButton.Context';
import { Button, Modal } from 'components/ui';
import { DetailedTaskForm } from 'horizon/components/DetailedTaskForm/DetailedTaskForm';
import { useImageViewerContext } from 'horizon/components/Images/ImageViewer/ImageViewerContext';
import {
  type TaskStep,
  ApplyTaskTemplateStepChooser,
} from 'horizon/routes/WorkOrders/ApplyTaskTemplateButton/ApplyTaskTemplateStepChooser';
import { useWCNotificationSubscriptionPrompt } from 'horizon/routes/WorkOrders/NotificationSubscriptions';
import { TaskFromTemplateForm } from 'horizon/routes/WorkOrders/Tasks/TaskTemplates/TaskFromTemplateForm/TaskFromTemplateForm';
import { TaskTemplatesChooser } from 'horizon/routes/WorkOrders/Tasks/TaskTemplates/TaskTemplatesChoosers';
import {
  type AtlasGqlTask,
  AtlasGqlTaskStatus,
  AtlasGqlWorkContainerTypeOption,
  useCreateTaskFromTemplateMutation,
  useCreateTaskMutation,
} from 'types/atlas-graphql';
import { INSPECT_TASK, REPAIR_TASK } from 'utils/constants';
import { useFeatures } from 'utils/features';
import { getTaskKeyFromType } from 'utils/helpers';
import { type IMenuClickevent } from 'utils/types';

import { CreateTaskButton, CreateTaskContainer, StyledMenu } from './CreateTask.style';
import { getRefetchQueriesForTaskCreation, getTaskTargetInput, messageContent } from './helpers';
import { type ICreateTaskProps, type ISaveTaskInput } from './types';

const DETAIL = 'DETAIL';
const REPAIR = 'REPAIR';
const INSPECT = 'INSPECT';

export const CreateTask: React.FunctionComponent<ICreateTaskProps> = ({
  className = '',
  damageId,
  forOrganization,
  damageBranchId,
  horizonDamageId,
  inspectionId,
  assetId = '',
  type = 'button',
  useMenuModal,
  ...props
}) => {
  const { t } = useTranslation();
  const { openPrompt } = useWCNotificationSubscriptionPrompt();
  const { setNavigationDisabled } = useImageViewerContext();
  const { setDisableShortcutKeys } = useContext(ImageNavButtonDisabledContext);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const queryRefetch = getRefetchQueriesForTaskCreation({ horizonDamageId, damageBranchId });

  const [createTask] = useCreateTaskMutation({
    onCompleted({ createTask: createdTask }) {
      if (!!createdTask) {
        const { id, number, __typename } = createdTask;
        openPrompt({
          content: messageContent({ id, number: number ?? 0, type: __typename ?? 'Task' }),
          workContainer: createdTask as AtlasGqlTask,
        });
      }

      setSaving(false);
    },
    onError: () => {
      setSaving(false);
    },
    refetchQueries: queryRefetch,
  });

  const [createTaskFromTemplate] = useCreateTaskFromTemplateMutation({
    onCompleted({ createTaskFromTemplate: createdTask }) {
      if (!!createdTask) {
        const { id, number, __typename } = createdTask;
        openPrompt({
          content: messageContent({ id, number: number ?? 0, type: __typename ?? 'Task' }),
          workContainer: createdTask as AtlasGqlTask,
        });
      }
    },
    refetchQueries: queryRefetch,
  });

  const [saving, setSaving] = useState(false);
  const [isTemplate, setIsTemplate] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [visible, setVisible] = useState(false);
  const [modalType, setModalType] = useState<undefined | string>(undefined);

  const { WORK_ORDERS, TASK_TEMPLATES } = useFeatures().features;

  const [showStepModal, setShowStepModal] = useState(false);
  const [pendingTemplateInput, setPendingTemplateInput] = useState<Record<string, any> | null>(
    null
  );
  const [selectedTaskSteps, setSelectedTaskSteps] = useState<TaskStep[]>([]);

  const saveTask = async (input: ISaveTaskInput) => {
    const { key } = input;
    setSaving(true);
    // @ts-ignore
    const taskKey: AtlasGqlWorkContainerTypeOption = getTaskKeyFromType(key);
    await createTask({
      variables: {
        input: {
          damageId,
          damageBranchId,
          ...(forOrganization && { forOrganization }),
          ...(horizonDamageId ? { horizonDamageId } : {}),
          assetId,
          type: taskKey,
          status: AtlasGqlTaskStatus.Pending,
          ...(inspectionId || horizonDamageId
            ? { targets: getTaskTargetInput({ inspectionId, horizonDamageId }) }
            : {}),
        },
      },
    });
  };

  function handleClose() {
    setNavigationDisabled(false);
    setDisableShortcutKeys(false);
    setIsTemplate(false);
    setVisible(false);
  }

  function handleTemplatesMenuItemClick(e: IMenuClickevent) {
    setIsTemplate(true);
    // Assume a key format of GROUP-NAME_TEMPLATE-ID
    const id = e.key.toString().split('_')[1];
    setTemplateId(id);
    if (!assetId) {
      setVisible(true);
    } else {
      const input = {
        id: id,
        damageBranchId,
        damageId,
        ...(horizonDamageId ? { horizonDamageId } : {}),
        assetId,
        ...(inspectionId || horizonDamageId
          ? { targets: getTaskTargetInput({ inspectionId, horizonDamageId }) }
          : {}),
      };
      setPendingTemplateInput(input);
      setSelectedTaskSteps([]);
      setShowStepModal(true);
    }
    setMenuOpen(false);
  }

  function handleCloseStepModal() {
    setShowStepModal(false);
    setPendingTemplateInput(null);
    setSelectedTaskSteps([]);
  }

  function handleStepModalConfirm() {
    if (pendingTemplateInput) {
      createTaskFromTemplate({
        variables: {
          input: {
            ...pendingTemplateInput,
            taskStepNames: selectedTaskSteps.map(s => s.name),
          },
        },
      });
    }
    handleCloseStepModal();
  }

  function handleRootMenuItemClick(key: string) {
    const splitKey = key.split('-');
    if (splitKey[0] === DETAIL) {
      setNavigationDisabled(true);
      setDisableShortcutKeys(true);
      setModalType(splitKey[1]);
      setVisible(true);
    } else {
      saveTask({ key });
    }
  }

  const dropDownMenu = useMemo(() => {
    if (type === 'menu') {
      return (
        <StyledMenu onClick={e => handleRootMenuItemClick(e)}>
          <Menu.Item {...props} id="create-unplanned-task-menu-repair" key="REPAIR">
            <PlusOutlined />
            {t('settings.repairTask', 'Repair Task')}
          </Menu.Item>
          {WORK_ORDERS && (
            <Menu.Item {...props} id="create-unplanned-task-menu-repair" key="DETAIL-REPAIR_TASK">
              <PlusOutlined />
              {t('workOrder.detailedRepairTask', 'Detailed Repair Task')}
            </Menu.Item>
          )}
          <Menu.Item {...props} id="create-unplanned-task-menu-inspect" key="INSPECT">
            <PlusOutlined />
            {t('settings.inspectTask', 'Inspect Task')}
          </Menu.Item>
          {WORK_ORDERS && (
            <Menu.Item {...props} id="create-unplanned-task-menu-inspect" key="DETAIL-INSPECT_TASK">
              <PlusOutlined />
              {t('workOrder.detailedInspectTask', 'Detailed Inspect Task')}
            </Menu.Item>
          )}
          {TASK_TEMPLATES && (
            <TaskTemplatesChooser
              displayType={'menu'}
              onClickHandler={handleTemplatesMenuItemClick}
              workContainerTypes={[
                AtlasGqlWorkContainerTypeOption.RepairTask,
                AtlasGqlWorkContainerTypeOption.InspectTask,
              ]}
            />
          )}
        </StyledMenu>
      );
    }

    return (
      <CreateTaskButton
        data-testid="create-unplanned-task-button"
        id="create-unplanned-task-button"
        title={t('workOrder.createNewTask', 'Create New Task')}
        className={className}
        onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => e.stopPropagation()}
      >
        <Dropdown
          trigger={['click']}
          onOpenChange={(open: boolean, { source }) => {
            if (source === 'trigger') {
              setMenuOpen(open);
            }
          }}
          open={menuOpen}
          menu={{
            items: [
              {
                key: REPAIR,
                label: t('workOrder.repair', 'Repair'),
              },
              WORK_ORDERS && {
                key: `${DETAIL}-${REPAIR_TASK}`,
                label: t('workOrder.detailedRepair', 'Detailed Repair'),
              },
              {
                key: INSPECT,
                label: t('workOrder.inspect', 'Inspect'),
              },
              WORK_ORDERS && {
                key: `${DETAIL}-${INSPECT_TASK}`,
                label: t('workOrder.detailedInspect', 'Detailed Inspect'),
              },
              TASK_TEMPLATES && {
                key: 'templates',
                label: (
                  <TaskTemplatesChooser
                    displayType={'menu'}
                    onClickHandler={handleTemplatesMenuItemClick}
                    workContainerTypes={[
                      AtlasGqlWorkContainerTypeOption.RepairTask,
                      AtlasGqlWorkContainerTypeOption.InspectTask,
                    ]}
                  />
                ),
              },
            ].filter((i): i is { key: string; label: JSX.Element } => !!i),
            onClick: ({ key }) => {
              if (key !== 'templates') {
                handleRootMenuItemClick(key);
                setMenuOpen(false);
              }
            },
          }}
        >
          <CreateTaskContainer>
            {t('table.createTasks', 'Create Task')}&nbsp;
            <DownOutlined />
          </CreateTaskContainer>
        </Dropdown>
      </CreateTaskButton>
    );
  }, [type, menuOpen, saving, WORK_ORDERS, TASK_TEMPLATES, horizonDamageId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (saving) {
    return type === 'menu' ? (
      <Menu.Item disabled {...props}>
        {t('workOrder.creating', 'Creating...')} <LoadingOutlined />
      </Menu.Item>
    ) : (
      <Button disabled>
        {t('workOrder.creating', 'Creating...')} <LoadingOutlined />
      </Button>
    );
  }

  return (
    <>
      {dropDownMenu}
      {showStepModal && pendingTemplateInput && (
        <Modal
          title={t('workOrder.chooseTaskSteps', 'Choose Task Steps')}
          open={showStepModal}
          onOk={handleStepModalConfirm}
          onCancel={handleCloseStepModal}
          destroyOnHidden={true}
        >
          <ApplyTaskTemplateStepChooser
            templateId={pendingTemplateInput.id}
            selectedTaskSteps={selectedTaskSteps}
            setSelectedTaskSteps={setSelectedTaskSteps}
            onNoSteps={handleStepModalConfirm}
          />
        </Modal>
      )}
      {modalType &&
        useMenuModal &&
        (isTemplate ? (
          <TaskFromTemplateForm
            title={t('workOrder.createTemplatedTask', 'Create Templated Task')}
            visible={visible}
            handleClose={handleClose}
            templateId={templateId}
            parentId={damageId}
            type={modalType}
            damageBranchId={damageBranchId}
            horizonDamageId={horizonDamageId}
            inspectionId={inspectionId}
            assetId={assetId}
          />
        ) : (
          <DetailedTaskForm
            title={
              modalType === REPAIR_TASK
                ? t('workOrder.createRepairTask', 'Create Repair Task')
                : t('workOrder.createInspectTask', 'Create Inspect Task')
            }
            visible={visible}
            handleClose={handleClose}
            parentId={damageId}
            type={modalType}
            damageBranchId={damageBranchId}
            horizonDamageId={horizonDamageId}
            inspectionId={inspectionId}
            assetId={assetId}
          />
        ))}
    </>
  );
};
